import React from "react";
import { PriorityBadge, HealthBadge, StatusBadge, fmtDate } from "../lib/ui";
import { useData } from "../lib/DataContext";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { ArrowUp, ArrowDown, ArrowUpDown, Star, ChevronUp, ChevronDown, EyeOff } from "lucide-react";

const COLS = [
  { key: "human_id", label: "Task ID" },
  { key: "title", label: "Task" },
  { key: "department_id", label: "Dept", skipCompact: true },
  { key: "project_id", label: "Project", skipCompact: true },
  { key: "current_action_owner_id", label: "Action Owner" },
  { key: "priority", label: "Priority" },
  { key: "execution_status", label: "Status" },
  { key: "health", label: "Health" },
  { key: "current_due_date", label: "Due" },
  { key: "progress", label: "Progress" },
];

function SortableTh({ col, sortBy, sortOrder, onSort }) {
  const active = sortBy === col.key;  const cls = "px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap";
  if (!onSort) return <th className={`${cls} text-left`}>{col.label}</th>;
  return (
    <th className={`${cls} text-left`}>
      <button type="button" data-testid={`sort-col-${col.key}`} onClick={() => onSort(col.key)}
        className={`flex items-center gap-1 uppercase tracking-wider text-xs font-semibold hover:text-slate-800 transition-colors ${active ? "text-blue-600" : "text-slate-500"}`}>
        {col.label}
        {active ? (sortOrder === -1 ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
      </button>
    </th>
  );
}

export default function TaskTable({ tasks, onOpen, selectable, selected = [], onSelect, compact, sortBy, sortOrder, onSort, hiddenCols,
  favourites, onToggleFav, reorderable, onMoveUp, onMoveDown, onDropReorder }) {
  const { userName, deptName, projName } = useData();
  const toggle = (id) => {
    if (selected.includes(id)) onSelect(selected.filter((x) => x !== id));
    else onSelect([...selected, id]);
  };
  if (!tasks) return null;
  if (tasks.length === 0) return <div className="text-center py-12 text-slate-400 text-sm">No tasks found.</div>;
  const cols = compact ? COLS.filter((c) => !c.skipCompact) : COLS.filter((c) => !(hiddenCols || []).includes(c.key));
  const favSet = favourites || new Set();
  const stop = (e) => e.stopPropagation();
  return (
    <div className="overflow-x-auto border rounded-lg bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-slate-50 text-left">
            {selectable && <th className="w-9 px-3 py-2"></th>}
            {onToggleFav && <th className="w-9 px-2 py-2"></th>}
            {cols.map((c) => (
              <SortableTh key={c.key} col={c} sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            ))}
            {reorderable && <th className="w-16 px-2 py-2 text-center text-xs font-semibold uppercase text-slate-500">Order</th>}
          </tr>
        </thead>
        <tbody>
          {tasks.map((t, i) => (
            <tr
              key={t.id}
              data-testid={`task-row-${t.human_id}`}
              onClick={() => onOpen(t.id)}
              draggable={!!(reorderable && onDropReorder)}
              onDragStart={reorderable && onDropReorder ? (e) => { e.dataTransfer.setData("text/plain", t.id); } : undefined}
              onDragOver={reorderable && onDropReorder ? (e) => e.preventDefault() : undefined}
              onDrop={reorderable && onDropReorder ? (e) => { e.preventDefault(); const mid = e.dataTransfer.getData("text/plain"); if (mid && mid !== t.id) onDropReorder(mid, t.id); } : undefined}
              className={`border-b last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors ${reorderable && onDropReorder ? "cursor-move" : ""}`}
            >
              {selectable && (
                <td className="px-3 py-2" onClick={stop}>
                  <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} />
                </td>
              )}
              {onToggleFav && (
                <td className="px-2 py-2" onClick={stop}>
                  <button data-testid={`fav-task-${t.human_id}`} onClick={() => onToggleFav(t.id)} title="Favourite"
                    className={favSet.has(t.id) ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}>
                    <Star size={16} className={favSet.has(t.id) ? "fill-amber-500" : ""} />
                  </button>
                </td>
              )}
              {!(hiddenCols || []).includes("human_id") && <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{t.human_id}</td>}
              {!(hiddenCols || []).includes("title") && (
                <td className="px-3 py-2 font-medium text-slate-800 max-w-xs truncate">
                  {t.is_hidden && <span data-testid={`hidden-badge-${t.human_id}`} className="inline-flex items-center gap-0.5 mr-1.5 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1 py-0.5 align-middle"><EyeOff size={10} /> Hidden</span>}
                  {t.title}
                </td>
              )}
              {!compact && !(hiddenCols || []).includes("department_id") && <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{deptName(t.department_id)}</td>}
              {!compact && !(hiddenCols || []).includes("project_id") && <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[10rem] truncate">{projName(t.project_id)}</td>}
              {!(hiddenCols || []).includes("current_action_owner_id") && <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{userName(t.current_action_owner_id)}</td>}
              {!(hiddenCols || []).includes("priority") && <td className="px-3 py-2"><PriorityBadge value={t.priority} /></td>}
              {!(hiddenCols || []).includes("execution_status") && <td className="px-3 py-2"><StatusBadge value={t.execution_status} /></td>}
              {!(hiddenCols || []).includes("health") && <td className="px-3 py-2"><HealthBadge value={t.health} /></td>}
              {!(hiddenCols || []).includes("current_due_date") && (
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                  {fmtDate(t.current_due_date)}
                  {t.days_overdue > 0 && <span className="text-red-600 text-xs ml-1">({t.days_overdue}d)</span>}
                </td>
              )}
              {!(hiddenCols || []).includes("progress") && (
                <td className="px-3 py-2 w-28">
                  <div className="flex items-center gap-2">
                    <Progress value={t.progress} className="h-1.5 w-14" />
                    <span className="text-xs text-slate-500">{t.progress}%</span>
                </div>
              </td>
              )}
              {reorderable && (
                <td className="px-2 py-2" onClick={stop}>
                  <div className="flex items-center justify-center gap-0.5">
                    <button data-testid={`move-up-${t.human_id}`} disabled={i === 0} onClick={() => onMoveUp(t.id)}
                      className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Move up"><ChevronUp size={15} /></button>
                    <button data-testid={`move-down-${t.human_id}`} disabled={i === tasks.length - 1} onClick={() => onMoveDown(t.id)}
                      className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Move down"><ChevronDown size={15} /></button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const TASK_COLUMNS = [
  { key: "human_id", label: "Task ID" }, { key: "title", label: "Task" },
  { key: "department_id", label: "Dept" }, { key: "project_id", label: "Project" },
  { key: "current_action_owner_id", label: "Action Owner" },
  { key: "priority", label: "Priority" }, { key: "execution_status", label: "Status" },
  { key: "health", label: "Health" }, { key: "current_due_date", label: "Due" },
  { key: "progress", label: "Progress" },
];

export function PageHeader({ title, subtitle, children, titleClass, headerExtra }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className={titleClass || "text-2xl font-bold tracking-tight text-slate-900"}>{title}</h1>
          {headerExtra}
        </div>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}
